import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, Home, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TerminosCondiciones = () => {
    const navigate = useNavigate();

    const aceptarTerminos = () => {
        localStorage.setItem('terminos_aceptados', new Date().toISOString());
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 md:p-12">
                {/* Breadcrumb */}
                <nav className="flex items-center text-sm text-gray-500 mb-8">
                    <Link to="/" className="hover:text-blue-600 transition-colors flex items-center gap-1">
                        <Home className="w-4 h-4" />
                        Inicio
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-gray-900 font-medium">Términos y Condiciones</span>
                </nav>

                {/* Header */}
                <div className="border-b-4 border-[#F59E0B] pb-6 mb-10">
                    <div className="flex items-center gap-3 mb-3">
                        <FileText className="w-10 h-10 text-[#1A3D5C]" />
                        <h1 className="text-4xl font-bold text-[#1A3D5C]">Términos y Condiciones</h1>
                    </div>
                    <p className="text-gray-500 text-sm">
                        Última actualización: <span className="font-semibold">6 de enero de 2026</span>
                    </p>
                </div>

                {/* Contenido Legal */}
                <div className="prose prose-lg max-w-none space-y-8 text-gray-700">
                    {/* Sección 1 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            1. DEFINICIONES Y ALCANCE
                        </h2>
                        <p className="text-justify leading-relaxed">
                            Estos Términos y Condiciones ("Términos") regulan el uso de la plataforma Servicios Futura
                            ("Plataforma"), disponible en www.serviciosfutura.com. Al acceder y utilizar esta Plataforma,
                            usted acepta estar sujeto a estos Términos. Si no está de acuerdo con alguno de los términos,
                            le recomendamos que no utilice la Plataforma.
                        </p>
                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1.1 Definiciones</h3>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li><strong>Servicios Futura:</strong> La plataforma digital de gestión inmobiliaria</li>
                            <li><strong>Usuario:</strong> Cualquier persona que acceda o use la Plataforma</li>
                            <li><strong>Agente:</strong> Profesional inmobiliario registrado en la Plataforma</li>
                            <li><strong>Propiedad:</strong> Bien raíz listado en la Plataforma</li>
                            <li><strong>Contenido:</strong> Cualquier información publicada por el Usuario</li>
                        </ul>
                    </section>

                    {/* Sección 2 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            2. USO DE LA PLATAFORMA
                        </h2>
                        <p className="text-justify leading-relaxed mb-4">
                            La Plataforma se proporciona "tal cual" para uso personal y profesional. El Usuario se compromete a:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li>Usar la Plataforma de manera legal y ética</li>
                            <li>No publicar contenido falso, engañoso o fraudulento</li>
                            <li>No interferir con la seguridad de la Plataforma</li>
                            <li>Respetar los derechos de propiedad intelectual</li>
                            <li>No transmitir virus, malware o código malicioso</li>
                        </ul>
                    </section>

                    {/* Sección 3 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            3. CUENTAS DE USUARIO
                        </h2>
                        <p className="text-justify leading-relaxed">
                            El Usuario es responsable de mantener la confidencialidad de su cuenta y contraseña.
                            Servicios Futura no es responsable de acceso no autorizado a su cuenta si usted no notifica
                            inmediatamente sobre cualquier uso no autorizado.
                        </p>
                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Información Requerida</h3>
                        <p className="text-justify leading-relaxed">
                            Para crear una cuenta, debe proporcionar información precisa, completa y actualizada.
                            Se compromete a actualizar su información si cambia.
                        </p>
                    </section>

                    {/* Sección 4 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            4. LISTADO DE PROPIEDADES
                        </h2>
                        <p className="text-justify leading-relaxed mb-4">
                            Los Agentes pueden listar propiedades en la Plataforma. Garantizan que:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li>Tienen autoridad legal para listar la propiedad</li>
                            <li>Toda la información es precisa y no engañosa</li>
                            <li>Las imágenes son de la propiedad real</li>
                            <li>Poseen los derechos de autor de todo el contenido</li>
                        </ul>
                        <p className="text-justify leading-relaxed mt-4">
                            Servicios Futura se reserva el derecho de remover listados que violen estos términos.
                        </p>
                    </section>

                    {/* Sección 5 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            5. PROPIEDAD INTELECTUAL
                        </h2>
                        <p className="text-justify leading-relaxed">
                            Todo contenido en la Plataforma, incluyendo logos, diseños, código y funcionalidades,
                            es propiedad de Servicios Futura o sus proveedores. El Usuario otorga a Servicios Futura
                            una licencia mundial, no exclusiva para usar el contenido que publica.
                        </p>
                    </section>

                    {/* Sección 6 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            6. LIMITACIÓN DE RESPONSABILIDAD
                        </h2>
                        <p className="text-justify leading-relaxed mb-4 font-semibold">
                            EN LA MEDIDA MÁXIMA PERMITIDA POR LA LEY:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                            <li>Servicios Futura no es responsable de daños indirectos, especiales o consecuentes</li>
                            <li>No garantiza que la Plataforma será ininterrumpida o libre de errores</li>
                            <li>No es responsable por la precisión de información de terceros</li>
                            <li>La responsabilidad total no excederá el monto pagado en el último mes</li>
                        </ul>
                    </section>

                    {/* Sección 7 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            7. CAMBIOS EN ESTOS TÉRMINOS
                        </h2>
                        <p className="text-justify leading-relaxed">
                            Servicios Futura se reserva el derecho de modificar estos Términos en cualquier momento.
                            El uso continuado de la Plataforma constituye aceptación de los Términos modificados.
                            Le notificaremos de cambios significativos por correo electrónico.
                        </p>
                    </section>

                    {/* Sección 8 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            8. CANCELACIÓN Y RESCISIÓN
                        </h2>
                        <p className="text-justify leading-relaxed">
                            Puede cancelar su cuenta en cualquier momento accediendo a su perfil.
                            Servicios Futura puede cancelar cuentas que violen estos Términos sin previo aviso.
                        </p>
                    </section>

                    {/* Sección 9 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            9. JURISDICCIÓN Y LEY APLICABLE
                        </h2>
                        <p className="text-justify leading-relaxed">
                            Estos Términos se rigen por las leyes de la República de El Salvador.
                            Cualquier disputa será resuelta en los tribunales competentes de El Salvador.
                        </p>
                    </section>

                    {/* Sección 10 */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#1A3D5C] mb-4 border-l-4 border-[#F59E0B] pl-4">
                            10. CONTACTO
                        </h2>
                        <p className="text-justify leading-relaxed mb-4">
                            Para preguntas sobre estos Términos, contáctenos en:
                        </p>
                        <ul className="list-none space-y-2 ml-4">
                            <li className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-600" />
                                <span><strong>Email:</strong> info@serviciosfutura.com</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-600" />
                                <span><strong>Email:</strong> info@serviciosfutura.com</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-600" />
                                <span><strong>Dirección:</strong> Av. Independencia Sur, Santa Ana, El Salvador</span>
                            </li>
                        </ul>
                    </section>
                </div>

                {/* Botones de Acción */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12 pt-8 border-t border-gray-200">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/')}
                        className="px-8 py-3 text-base"
                    >
                        Volver al Inicio
                    </Button>
                    <Button
                        onClick={aceptarTerminos}
                        className="px-8 py-3 text-base bg-[#1A3D5C] hover:bg-[#152e45] text-white"
                    >
                        <Check className="w-5 h-5 mr-2" />
                        Aceptar y Continuar
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TerminosCondiciones;
